import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, Save } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateChallenge, useMfaStatus, useVerifyCode } from '@/features/mfa/hooks/use-mfa';
import { supabase } from '@/lib/supabase';
import { useMe, useUpdateMe, type Me } from '@/lib/me';

export const Route = createFileRoute('/_app/settings/account')({
  component: AccountPage,
});

// Lista corta de timezones útiles para LATAM. Si en el futuro hay
// usuarios fuera, ampliar. Persistencia por ahora SOLO localStorage —
// no entra al backend hasta que haya valor de negocio (ver doc).
const TIMEZONES = [
  { value: 'America/Santiago', label: 'America/Santiago (Chile, default)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'America/Argentina/Buenos_Aires' },
  { value: 'America/Lima', label: 'America/Lima' },
  { value: 'America/Bogota', label: 'America/Bogotá' },
  { value: 'America/Mexico_City', label: 'America/México DF' },
  { value: 'UTC', label: 'UTC' },
] as const;

const LANGUAGES = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English (próximamente)' },
] as const;

const PREFS_KEY = 'simplificalo:user-prefs';

interface UserPrefs {
  language: string;
  timeZone: string;
}

function readPrefs(): UserPrefs {
  if (typeof window === 'undefined') return { language: 'es', timeZone: 'America/Santiago' };
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<UserPrefs>;
      return {
        language: parsed.language ?? 'es',
        timeZone: parsed.timeZone ?? 'America/Santiago',
      };
    }
  } catch {
    /* ignore corrupted */
  }
  return { language: 'es', timeZone: 'America/Santiago' };
}

function writePrefs(p: UserPrefs): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
}

function AccountPage() {
  const { data: me, isLoading, error } = useMe();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Mi cuenta</h1>
        <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
          Tu perfil personal: nombre, contacto, preferencias.
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : error ? (
        <div
          className="rounded-lg border px-3 py-2 text-xs"
          style={{
            background: 'color-mix(in srgb, var(--color-danger, #ef4444) 8%, transparent)',
            borderColor: 'color-mix(in srgb, var(--color-danger, #ef4444) 30%, transparent)',
          }}
        >
          Error al cargar tu perfil: {error.message}
        </div>
      ) : !me ? null : (
        <AccountForm me={me} />
      )}
    </div>
  );
}

function AccountForm({ me }: { readonly me: Me }) {
  const update = useUpdateMe();

  const [fullName, setFullName] = useState(me.fullName ?? '');
  const [phone, setPhone] = useState(me.phone ?? '');
  const initialPrefs = readPrefs();
  const [language, setLanguage] = useState(initialPrefs.language);
  const [timeZone, setTimeZone] = useState(initialPrefs.timeZone);

  useEffect(() => {
    setFullName(me.fullName ?? '');
    setPhone(me.phone ?? '');
  }, [me]);

  const handle = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Server-side: name + phone (entran al perfil compartido).
    update.mutate(
      {
        fullName: fullName.trim() || null,
        phone: phone.trim() || null,
      },
      {
        onSuccess: () => {
          // Local-only: language + timeZone (preferencia del navegador
          // del usuario; no hay valor server-side hasta tener emails
          // localizados).
          writePrefs({ language, timeZone });
          toast.success('Perfil actualizado');
        },
        onError: (err) => toast.error(`Error al guardar: ${err.message}`),
      },
    );
  };

  return (
    <form onSubmit={handle} className="space-y-6">
      {/* Avatar placeholder */}
      <section
        className="flex items-center gap-4 rounded-lg border p-4"
        style={{
          background: 'var(--color-bg-elevated)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-semibold text-white"
          style={{ background: 'var(--color-primary)' }}
          aria-hidden
        >
          {(me.fullName ?? me.email ?? '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="text-xs text-[color:var(--color-fg-muted)]">
          <strong>Próximamente foto upload.</strong> Por ahora se muestran tus
          iniciales generadas a partir de tu nombre.
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold">Datos personales</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="acc-name">Nombre completo</Label>
            <Input
              id="acc-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Felipe Silva"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="acc-email">Email</Label>
            <Input
              id="acc-email"
              type="email"
              value={me.email ?? ''}
              readOnly
              disabled
              title="El email no se puede cambiar desde acá. Contactá a un owner."
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="acc-phone">Teléfono</Label>
            <Input
              id="acc-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+56 9 1234 5678"
            />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold">Preferencias</h2>
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          Idioma y zona horaria se guardan localmente en tu navegador (no
          en el servidor). Si entrás desde otro dispositivo verás los
          defaults.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="acc-lang">Idioma</Label>
            <select
              id="acc-lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="h-9 rounded-md border bg-[color:var(--color-bg)] border-[color:var(--color-border)] px-2 text-sm"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value} disabled={l.value !== 'es'}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="acc-tz">Zona horaria</Label>
            <select
              id="acc-tz"
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              className="h-9 rounded-md border bg-[color:var(--color-bg)] border-[color:var(--color-border)] px-2 text-sm"
            >
              {TIMEZONES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Seguridad</h2>
        <ChangePasswordButton email={me.email ?? ''} />
      </section>

      <div
        className="flex justify-end gap-2 border-t pt-4"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <Button type="submit" disabled={update.isPending}>
          <Save className="mr-1.5 h-4 w-4" aria-hidden />
          {update.isPending ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>
    </form>
  );
}

function ChangePasswordButton({ email }: { readonly email: string }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [pending, setPending] = useState(false);
  // MFA: need to elevate session to AAL2 before Supabase allows password change.
  const { data: mfaStatus } = useMfaStatus();
  const challengeMutation = useCreateChallenge();
  const verifyMutation = useVerifyCode();
  const challengeRef = useRef<{ challenge_id: string; factor_id: string } | null>(null);
  const needsMfa = mfaStatus?.enrolled === true;

  const close = () => {
    setPwd('');
    setPwd2('');
    setTotpCode('');
    challengeRef.current = null;
    setOpen(false);
  };

  // Create MFA challenge when dialog opens (if MFA is enrolled).
  useEffect(() => {
    if (!open || !needsMfa || !mfaStatus?.factor_id) return;
    challengeMutation
      .mutateAsync({ factor_id: mfaStatus.factor_id })
      .then((res) => {
        challengeRef.current = res;
      })
      .catch(() => {
        toast.error('No se pudo iniciar la verificación MFA. Intentá de nuevo.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsMfa, mfaStatus?.factor_id]);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (needsMfa && totpCode.length !== 6) {
      toast.error('Ingresá el código de 6 dígitos de tu authenticator');
      return;
    }
    setPending(true);
    try {
      // Step 1: If MFA is enabled, verify TOTP to elevate session to AAL2.
      if (needsMfa) {
        if (!challengeRef.current) {
          toast.error('Challenge MFA no disponible. Cerrá y volvé a abrir el diálogo.');
          return;
        }
        const verifyRes = await verifyMutation.mutateAsync({
          factor_id: challengeRef.current.factor_id,
          challenge_id: challengeRef.current.challenge_id,
          code: totpCode,
        });
        // Set the AAL2 session so updateUser works.
        await supabase.auth.setSession({
          access_token: verifyRes.access_token,
          refresh_token: verifyRes.refresh_token,
        });
      }

      // Step 2: Change password (now with AAL2 if MFA was required).
      const { error: err } = await supabase.auth.updateUser({ password: pwd });
      if (err) throw err;

      toast.success('Contraseña actualizada. Redirigiendo al login…');
      close();
      await supabase.auth.signOut();
      window.location.href = '/login';
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'desconocido';
      const status = (err as { status?: number }).status;
      if (status === 401 || msg.includes('session_not_found') || msg.includes('not authenticated')) {
        toast.error('Tu sesión expiró. Redirigiendo al login…');
        await supabase.auth.signOut();
        window.location.href = '/login';
        return;
      }
      if (msg.includes('insufficient_aal')) {
        toast.error('Código MFA incorrecto o expirado. Intentá de nuevo.');
        setTotpCode('');
        // Refresh the challenge for retry.
        if (mfaStatus?.factor_id) {
          challengeMutation
            .mutateAsync({ factor_id: mfaStatus.factor_id })
            .then((res) => { challengeRef.current = res; })
            .catch(() => {});
        }
        return;
      }
      toast.error(`Error: ${msg}`);
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Button variant="outline" type="button" onClick={() => setOpen(true)}>
        <KeyRound className="mr-1.5 h-4 w-4" aria-hidden />
        Cambiar contraseña
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
            <DialogDescription>
              Cambio inmediato para <code className="text-xs">{email}</code>. Las
              sesiones activas en otros dispositivos quedan vivas hasta que se
              renueven (24h máximo).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pwd-new">Nueva contraseña</Label>
              <Input
                id="pwd-new"
                type="password"
                autoComplete="new-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pwd-confirm">Repetir contraseña</Label>
              <Input
                id="pwd-confirm"
                type="password"
                autoComplete="new-password"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                required
                minLength={8}
              />
            </div>
            {needsMfa && (
              <div className="flex flex-col gap-1.5 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                <Label htmlFor="pwd-totp">Código MFA (authenticator)</Label>
                <Input
                  id="pwd-totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center font-mono text-lg tracking-[0.5em]"
                />
                <p className="text-xs text-[color:var(--color-fg-muted)]">
                  Supabase requiere verificación MFA para cambiar la contraseña.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancelar
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? 'Cambiando…' : 'Cambiar contraseña'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

